import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../hooks/useData";
import { PageHeader, Tabs, DataTable, KpiCard, StatusBadge, Button } from "../components/shared";
import { money, num, fmtDate } from "../lib/api";
import { Layers, IndianRupee, Boxes, Shirt, Puzzle } from "lucide-react";

const WIP_PROCESSES = ["Cutting", "Embroidery", "Printing", "Emboss", "Stitching", "Bartec", "Washing", "Thread Cutting", "Checking", "Finishing"];
const ACC_CATEGORIES = ["Elastic", "Pocketing", "Size Label", "Patches", "Dori", "Tags", "Box/Carton", "Polly", "Others", "Rivet/Button", "Thread"];

export default function StockInHand() {
  const [tab, setTab] = useState("Fabric");
  return (
    <div>
      <PageHeader title="Stock In Hand" subtitle="Consolidated inventory across the factory" />
      <Tabs testid="stock-tabs" tabs={["Fabric", "WIP", "Finished Goods", "Accessories"]} active={tab} onChange={setTab} />
      {tab === "Fabric" && <FabricTab />}
      {tab === "WIP" && <WipTab />}
      {tab === "Finished Goods" && <FgTab />}
      {tab === "Accessories" && <AccTab />}
    </div>
  );
}

function FabricTab() {
  const { data } = useData("/fabric-lots");
  const rows = data || [];
  const qty = rows.reduce((s, l) => s + (l.balance_qty || 0), 0);
  const val = rows.reduce((s, l) => s + (l.balance_qty || 0) * (l.rate || 0), 0);
  const columns = [
    { key: "purchase_date", header: "Purchase Date", render: (r) => fmtDate(r.purchase_date) },
    { key: "bill_number", header: "Bill No" }, { key: "supplier_name", header: "Supplier" },
    { key: "lot_number", header: "Lot No", mono: true }, { key: "colour", header: "Colour" },
    { key: "fabric_type", header: "Fabric" }, { key: "rolls", header: "Rolls", align: "right" },
    { key: "balance_qty", header: "Balance", align: "right", render: (r) => `${num(r.balance_qty)} ${r.unit}` },
    { key: "rate", header: "Rate", align: "right", render: (r) => num(r.rate) },
    { key: "value", header: "Value", align: "right", render: (r) => money((r.balance_qty || 0) * (r.rate || 0)) },
  ];
  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <KpiCard label="Total Quantity" value={num(qty)} icon={Layers} accent="blue" />
        <KpiCard label="Total Value" value={money(val)} icon={IndianRupee} accent="emerald" />
        <KpiCard label="Number of Lots" value={num(rows.length)} icon={Boxes} accent="sky" />
      </div>
      <DataTable testid="stock-fabric-table" columns={columns} rows={rows} exportName="stock-fabric.csv" />
    </div>
  );
}

function WipTab() {
  const { data } = useData("/wip");
  const navigate = useNavigate();
  const [proc, setProc] = useState("Cutting");
  if (!data) return null;
  const all = Object.values(data).flat();
  const totalPcs = all.reduce((s, r) => s + (r.available || 0), 0);
  const designs = new Set(all.map((r) => r.design_number)).size;
  const rows = data[proc] || [];
  const columns = [
    { key: "production_code", header: "Prod Order", mono: true }, { key: "design_number", header: "Design" },
    { key: "colour", header: "Colour" }, { key: "item_type", header: "Item" },
    { key: "available", header: "Available", align: "right", render: (r) => <span className="font-semibold text-emerald-700">{num(r.available)}</span> },
    { key: "pending", header: "With Jobber", align: "right", render: (r) => num(r.pending) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "act", header: "", sortable: false, render: (r) => r.available > 0 ? <Button size="sm" onClick={() => navigate("/process-issue", { state: { orderId: r.production_order_id, from: proc } })} data-testid={`stock-wip-issue-${r.production_order_id}`}>Issue / Challan</Button> : null },
  ];
  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <KpiCard label="Total WIP Pieces" value={num(totalPcs)} icon={Boxes} accent="sky" />
        <KpiCard label="WIP Value (est.)" value={money(totalPcs * 150)} icon={IndianRupee} accent="violet" />
        <KpiCard label="Number of Designs" value={num(designs)} icon={Layers} accent="blue" />
      </div>
      <Tabs testid="stock-wip-tabs" tabs={WIP_PROCESSES.map((p) => ({ value: p, label: `${p} (${(data[p] || []).length})` }))} active={proc} onChange={setProc} />
      <DataTable testid="stock-wip-table" columns={columns} rows={rows} exportName={`stock-wip-${proc}.csv`} />
    </div>
  );
}

function FgTab() {
  const { data } = useData("/finished-goods");
  const rows = (data || []).filter((f) => (f.balance_qty ?? f.quantity) > 0);
  const qty = rows.reduce((s, f) => s + (f.balance_qty ?? f.quantity ?? 0), 0);
  const val = rows.reduce((s, f) => s + (f.value || 0), 0);
  const columns = [
    { key: "date", header: "Date", render: (r) => fmtDate(r.date) }, { key: "design_number", header: "Design" },
    { key: "colour", header: "Colour" }, { key: "item_type", header: "Item" },
    { key: "balance_qty", header: "In Stock", align: "right", render: (r) => num(r.balance_qty ?? r.quantity) },
    { key: "value", header: "Value", align: "right", render: (r) => money(r.value) },
    { key: "location", header: "Location" },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 gap-4 mb-4 max-w-lg">
        <KpiCard label="Total Quantity" value={num(qty)} icon={Shirt} accent="emerald" />
        <KpiCard label="Total Value" value={money(val)} icon={IndianRupee} accent="emerald" />
      </div>
      <DataTable testid="stock-fg-table" columns={columns} rows={rows} exportName="stock-fg.csv" />
    </div>
  );
}

function AccTab() {
  const { data } = useData("/accessories");
  const [cat, setCat] = useState("Elastic");
  const rows = (data || []).filter((a) => a.category === cat);
  const totalVal = (data || []).reduce((s, a) => s + (a.stock || 0) * (a.rate || 0), 0);
  const columns = [
    { key: "name", header: "Item", render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "unit", header: "Unit" }, { key: "stock", header: "Stock", align: "right", render: (r) => num(r.stock) },
    { key: "rate", header: "Rate", align: "right", render: (r) => num(r.rate) },
    { key: "value", header: "Value", align: "right", render: (r) => money((r.stock || 0) * (r.rate || 0)) },
  ];
  return (
    <div>
      <div className="max-w-xs mb-4"><KpiCard label="Total Accessories Value" value={money(totalVal)} icon={Puzzle} accent="amber" /></div>
      <Tabs testid="stock-acc-tabs" tabs={ACC_CATEGORIES} active={cat} onChange={setCat} />
      <DataTable testid="stock-acc-table" columns={columns} rows={rows} exportName={`stock-acc-${cat}.csv`} />
    </div>
  );
}
